import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { firstValueFrom, Subject, takeUntil } from "rxjs";

import { AuthService } from "@bitwarden/common/abstractions/auth.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { NotificationsService } from "@bitwarden/common/abstractions/notifications.service";
import { AuthenticationStatus } from "@bitwarden/common/enums/authenticationStatus";

import {
  PendingDeviceApprovalDialogComponent,
  PendingDeviceApprovalDialogData,
  PendingDeviceApprovalDialogResult,
} from "../../../gloss/others/pending-device-approval-dialog.component";

import { DeviceApprovalService } from "./device-approval.service";
import { keepDialogEscapeDisabled } from "@bitwarden/web-vault/app/services/dialog-escape.service";

/**
 * TDE new-device approval, approver side (GOOGLE_SSO_PLAN.md, Phase 3). On an already-trusted,
 * unlocked device, looks for pending approval requests and shows them one at a time in
 * `PendingDeviceApprovalDialogComponent`. Checks on a `GlossDeviceApprovalRequested` push, when
 * the tab becomes visible or the window regains focus, and whenever `app.component` reports the
 * vault as unlocked/ready. Focus matters when the websocket is down: switching between two
 * visible windows never fires `visibilitychange`.
 * The push only triggers a re-fetch — nothing in its payload is trusted.
 */
@Injectable({
  providedIn: "root",
})
export class DeviceApprovalWatcherService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private checking = false;
  private recheckRequested = false;
  // Requests the user closed without answering — not re-shown this session, so every tab
  // focus doesn't re-open the same dialog. They stay pending server-side until they expire.
  private dismissedIds = new Set<string>();
  // Bumped by reset() so a pass still running from before a lock/logout stops instead of showing
  // what it fetched for that session.
  private session = 0;
  private dialogRef?: MatDialogRef<
    PendingDeviceApprovalDialogComponent,
    PendingDeviceApprovalDialogResult
  >;

  constructor(
    private deviceApprovalService: DeviceApprovalService,
    private authService: AuthService,
    private notificationsService: NotificationsService,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private logService: LogService,
  ) {
    this.notificationsService.deviceApprovalRequested$
      ?.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.ngZone.run(() => void this.check()));

    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("focus", this.onWindowFocus);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("focus", this.onWindowFocus);
  }

  /**
   * Shows pending requests until none are left. A call arriving while one is already running
   * (e.g. a push while a dialog is open) is coalesced into one more pass afterwards.
   */
  async check(): Promise<void> {
    if (this.checking) {
      this.recheckRequested = true;
      return;
    }
    this.checking = true;
    try {
      do {
        this.recheckRequested = false;
        await this.showPendingRequests();
      } while (this.recheckRequested);
    } catch (e) {
      this.logService.error(e);
    } finally {
      this.checking = false;
    }
  }

  /**
   * Called on lock and logout. Closes any open dialog (it's `disableClose` and router navigation
   * doesn't close it) and ends the running pass, so nothing carries over to the lock screen or to
   * the next account on this browser.
   */
  reset() {
    this.session++;
    this.dismissedIds.clear();
    this.dialogRef?.close();
  }

  private onVisibilityChange = (): void => {
    if (document.visibilityState === "visible") {
      void this.check();
    }
  };

  private onWindowFocus = (): void => {
    void this.check();
  };

  private async showPendingRequests(): Promise<void> {
    const session = this.session;
    if (!(await this.canApprove())) {
      return;
    }

    const pending = await this.deviceApprovalService.listPending();
    for (const request of pending) {
      if (this.dismissedIds.has(request.id)) {
        continue;
      }
      // Re-check between dialogs: the user may have locked or logged out meanwhile.
      if (!(await this.canApprove())) {
        return;
      }

      const fingerprintPhrase = await this.deviceApprovalService.getFingerprintPhrase(
        request.publicKey,
      );
      if (session !== this.session) {
        return;
      }
      this.dialogRef = this.dialog.open<
        PendingDeviceApprovalDialogComponent,
        PendingDeviceApprovalDialogData,
        PendingDeviceApprovalDialogResult
      >(PendingDeviceApprovalDialogComponent, {
        panelClass: "cat-notification-dialog",
        disableClose: true,
        width: "600px",
        maxWidth: "95vw",
        data: { request, fingerprintPhrase },
      });

      // A pending login approval needs an explicit Approve/Deny/Dismiss; an Escape close would
      // count as none of them and just show the request again.
      keepDialogEscapeDisabled(this.dialogRef);
      const result = await firstValueFrom(this.dialogRef.afterClosed());
      this.dialogRef = undefined;
      if (session !== this.session) {
        return;
      }
      // Only an explicit dismiss counts; a dialog closed by reset() shows again after unlock.
      if (result === "dismissed") {
        this.dismissedIds.add(request.id);
      }
    }
  }

  private async canApprove(): Promise<boolean> {
    if ((await this.authService.getAuthStatus()) !== AuthenticationStatus.Unlocked) {
      return false;
    }
    return this.deviceApprovalService.isTrustedDevice();
  }
}
